import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/axios';
import { track as gaTrack } from '@/lib/analytics';

const device = () => {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1280) return 'tablet';
  return 'desktop';
};

const track = (campaignId, tipo) => {
  api.post('/marketplace/publicidade/evento', { campaign_id: campaignId, tipo, dispositivo: device() })
    .catch(() => {});
};

const useAds = (posicao, { pagina = '', distrito = '', categoria = '', limite = 1 } = {}) => {
  const [ads, setAds] = useState([]);
  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams({ posicao, dispositivo: device(), limite: String(limite) });
    if (pagina) params.set('pagina', pagina);
    if (distrito) params.set('distrito', distrito);
    if (categoria) params.set('categoria', categoria);
    api.get(`/marketplace/publicidade/servir?${params.toString()}`)
      .then((r) => { if (alive) setAds(r.data.items || []); })
      .catch(() => { if (alive) setAds([]); });
    return () => { alive = false; };
  }, [posicao, pagina, distrito, categoria, limite]);
  return ads;
};

const AdCreative = ({ ad, posicao, className = '', compact = false }) => {
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || seen.current) return undefined;
    const key = `mp_ad_imp_${ad.id}_${posicao}`;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !seen.current) {
          seen.current = true;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, '1');
            track(ad.id, 'impressao');
            gaTrack('view_advertisement', { posicao });
          }
          obs.disconnect();
        }
      });
    }, { threshold: 0.5 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [ad.id, posicao]);

  const isMobile = device() === 'mobile';
  const src = isMobile ? (ad.imagem_mobile || ad.imagem_desktop) : ad.imagem_desktop;

  return (
    <a
      ref={ref}
      href={ad.link}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      onClick={() => { track(ad.id, 'clique'); gaTrack('click_advertisement', { posicao }); }}
      className={`group relative block overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-emerald-300 hover:shadow-md ${className}`}
      data-testid={`ad-slot-${posicao}`}
      data-ad-id={ad.id}
    >
      <span className="absolute left-2 top-2 z-10 rounded-full bg-slate-900/75 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
        {ad.rotulo || 'Publicidade'}
      </span>
      <img src={src} alt={ad.texto || 'Publicidade'} loading="lazy" referrerPolicy="no-referrer"
        className={`w-full object-cover ${compact ? 'h-[420px]' : 'h-auto'}`} />
      {(ad.texto || ad.botao) && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          {ad.texto && <span className="text-xs font-semibold text-slate-700">{ad.texto}</span>}
          {ad.botao && (
            <span className="rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white transition group-hover:bg-emerald-700">
              {ad.botao}
            </span>
          )}
        </div>
      )}
    </a>
  );
};

/** Espaço publicitário simples. Não renderiza nada quando não há campanha. */
export const AdSlot = ({ posicao, pagina = '', distrito = '', categoria = '', className = '', onlyDesktop = false }) => {
  const ads = useAds(posicao, { pagina, distrito, categoria });
  if (onlyDesktop && device() !== 'desktop') return null;
  if (!ads.length) return null;
  return (
    <div className={`my-4 ${className}`} data-testid={`ad-wrapper-${posicao}`}>
      <AdCreative ad={ads[0]} posicao={posicao} />
    </div>
  );
};

/** Coluna lateral: só aparece em ecrãs largos e quando existe campanha. */
export const AdSideRail = ({ posicao, className = '' }) => {
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1536);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1536);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const ads = useAds(posicao);
  if (!wide || !ads.length) return null;
  return (
    <aside className={`hidden w-[160px] shrink-0 2xl:block ${className}`} data-testid={`ad-rail-${posicao}`}>
      <div className="sticky top-24">
        <AdCreative ad={ads[0]} posicao={posicao} compact />
      </div>
    </aside>
  );
};

/**
 * Publicidade entre resultados: nunca substitui anúncios reais nem altera a paginação.
 * A frequência é configurada pelo administrador.
 */
export const AdInlineList = ({ posicao, children, pagina = '', distrito = '' }) => {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => {
    api.get('/marketplace/publicidade/config')
      .then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  const max = cfg?.max_por_pagina || 1;
  const ads = useAds(posicao, { pagina, distrito, limite: max });

  const nodes = useMemo(() => {
    const items = React.Children.toArray(children);
    if (!cfg || !ads.length) return items;
    const dev = device();
    if (!cfg[dev]) return items;
    const every = Math.max(3, cfg.insert_every_n_items || 10);
    const first = Math.max(1, cfg.primeira_posicao || 4);
    const out = [];
    let used = 0;
    items.forEach((node, i) => {
      out.push(node);
      const pos = i + 1;
      const shouldInsert = pos === first || (pos > first && (pos - first) % every === 0);
      if (shouldInsert && used < Math.min(max, ads.length) && pos < items.length) {
        const ad = ads[used];
        out.push(
          <AdCreative key={`ad-${ad.id}-${pos}`} ad={ad} posicao={posicao} className="h-full" />,
        );
        used += 1;
      }
    });
    return out;
  }, [children, cfg, ads, posicao]);

  return <>{nodes}</>;
};

export default AdSlot;
